import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Select } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import { updateCallLocationSchema, UpdateCallLocationSchemaInput, UpdateCallLocationSchemaOutput } from "view/calls/utils/call.schema";

import { UpdateCallLocationDocument } from "__generated__/view/calls/graphql/call.mutations.generated";

const UpdateCallLocationDialog = ({ open, onClose, call, locations, refetch }: {
	open: boolean,
	onClose: () => void,
	call: {
		id: number;
		location: LocationSelectOption | null;
	}
	locations: LocationSelectOption[],
	refetch: () => void,
}) => {

	const { control, handleSubmit, reset } = useForm<UpdateCallLocationSchemaInput, unknown, UpdateCallLocationSchemaOutput>({
		resolver: zodResolver(updateCallLocationSchema),
		defaultValues: {
			location: null,
		},
	});

	const handleOnClose = () => {
		onClose();
		reset({
			location: null,
		});
	};

	const [ updateCallLocation, { loading } ] = useMutation(UpdateCallLocationDocument);

	const save = (data: UpdateCallLocationSchemaOutput) => {
		updateCallLocation({
			variables: {
				id: call.id,
				locationId: data.location.id,
			},
		}).then(() => {
			refetch();
			handleOnClose();
		});
	};

	useEffect(() => {
		if (open) {
			reset({
				location: call.location,
			});
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "LOCATION" })}
			form='updateCallLocationForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="updateCallLocationForm">
				<FormGroup label={intl.formatMessage({ id: "LOCATION" })} overflow="hidden" required>
					<Controller
						name="location"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Select
									{ ...field }
									fetchOptions={locations}
									getOptionLabel={option => option?.description || ""} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default UpdateCallLocationDialog;
